/**
 * Checks whether a value is `null` or `undefined`.
 *
 * Useful for filtering out optional values or defaulting behavior.
 *
 * @param value - The value to check.
 * @returns `true` if the value is `null` or `undefined`, otherwise `false`.
 *
 * @example
 * isNil(null);      // true
 * isNil(undefined); // true
 * isNil(0);         // false
 * isNil('');        // false
 */
export function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Checks whether a value is **not** `null` or `undefined`.
 *
 * Often used as a type guard to narrow types or filter arrays.
 *
 * @param value - The value to check.
 * @returns `true` if the value is not `null` or `undefined`, otherwise `false`.
 *
 * @example
 * isDefined(0);         // true
 * isDefined('hello');   // true
 * isDefined(null);      // false
 * isDefined(undefined); // false
 *
 * // Example usage with Array.prototype.filter:
 * const values = [1, null, 2, undefined, 3];
 * const definedOnly = values.filter(isDefined); // [1, 2, 3]
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Checks whether a value can be safely interpreted as a finite number.
 *
 * This includes:
 * - finite numbers (e.g., 123, 3.14, -5)
 * - numeric strings (e.g., '42', ' 3.5 ', '-0')
 *
 * Excludes:
 * - NaN, Infinity, -Infinity
 * - non-numeric strings ('abc', '1a', '')
 * - null, undefined, and whitespace-only strings
 *
 * This is useful when determining if a string can be safely coerced
 * to a number for sorting or arithmetic.
 *
 * @param value - The value to test.
 * @returns `true` if the value is numeric.
 *
 * @example
 * isNumeric(123);      // true
 * isNumeric('123');    // true
 * isNumeric(' 3.14 '); // true
 * isNumeric('abc');    // false
 * isNumeric(null);     // false
 */
export function isNumeric(value: number | string | null | undefined): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (isNil(value)) return false;
  if (/^\s*$/.test(value)) return false;

  const coerced = +value;
  return typeof coerced === 'number' && Number.isFinite(coerced);
}
